"use client";

import { type JSX, type ReactNode, useEffect, useRef, useState } from "react";
import { MagnifyingGlass } from "@phosphor-icons/react";
import type { FieldValues, Path, UseFormReturn } from "react-hook-form";

import { Button } from "@/shared/components/common/button";
import Flex from "@/shared/components/common/Flex";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/common/form";
import Grid from "@/shared/components/common/Grid";
import Typography from "@/shared/components/common/Typography";
import { cn, scrollToElement } from "@/shared/libs/commonUtils";
import { formatInputs } from "@/shared/libs/componentUtils";
import { formatIntoJalaliFunc } from "@/shared/libs/date";
import CustomInput from "../CustomInput";
import LocationSwitch from "./flight/components/LocationSwitch";
import json from "@/public/data/mainForm.json";
import { Pulse } from "@/shared/components/common/Animations";

import { useGlobalStore } from "@/shared/stores/global-store/GlobalStoreProvider";
import { usePathname,useSearchParams } from "next/navigation";

type TabBodyProps<
  TFormField extends Path<ServiceSchema>,
  ServiceSchema extends FieldValues
> = {
  mobile?: boolean,
  services: "flight" | "hotel" | "tour" | "train" | "bus";
  page?: "landing" | "result" | "passengers" | "confirm";
  form: UseFormReturn<ServiceSchema, unknown, undefined>;
  hasLocationSwitch?: boolean;
  onSubmit: (data: ServiceSchema | undefined) => void;
  className: (id: number, type: string) => string;
  headerButton?: { component: ReactNode; selected: unknown };
  inputConfig: {
    progressInputs: {
      openStatus: {
        open: boolean;
        id: number;
      }[];
      handleProgress: (id: number, isValueSet?: boolean) => void;
    };
    icons: {
      [key: string]: ReactNode;
    };
    inputs: (
      key: string,
      paramName: TFormField,
      id: number,
      func?: (value: unknown) => string
    ) => JSX.Element;
  };
};

export default function TabContent<
  TFormField extends Path<ServiceSchema>,
  ServiceSchema extends FieldValues
>({
  mobile,
  page,
  form,
  services,
  className,
  headerButton,
  hasLocationSwitch,
  inputConfig: {
    progressInputs: { handleProgress, openStatus },
    icons,
    inputs,
  },
  onSubmit,
}: TabBodyProps<TFormField, ServiceSchema>) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { formsInputs, searchButton, title } = json[services];
  const filteredFormInputs = formsInputs.filter(
    (field) => field.name === "checkin" || field.name === "checkout"
  );
  const formInputsBaseOnDevice = mobile ? filteredFormInputs : formsInputs; 
  const { setIsDrawerOpen } = useGlobalStore((state) => state);
  const tabRef = useRef<HTMLDivElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    watch,
    formState: { isSubmitting },
  } = form;

  useEffect(() => {
    if ( pathname !== "/") {
      setIsLoading(false);
    }
  }, [pathname,searchParams]);

  // Update loading state
  useEffect(() => {
    if ( isSubmitting && !isLoading) {
      setIsLoading(true);
    }
  }, [isSubmitting,isLoading]);

  return (
    <Flex
      id={`${services}_tab`}
      ref={tabRef}
      className={cn(
        "flex-col bg-background max-md:w-full border-[1px] border-t-0 border-border rounded-bl-xs rounded-br-xs",
        page === "result"
          ? "rounded-xs md:-translate-y-1/2  shadow-1 max-md:mb-14"
          : ""
      )}
    >
      <Typography variant="body1" className="md:hidden absolute top-9 right-14">
        {title}
      </Typography>
      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="w-full">
          {/* button type switch */}
          {headerButton?.component}
          {/* Inputs */}
          <Flex className="!w-full h-fit py-3 px-6 mx-auto">
            <Grid className="w-full min-w-full grid-cols-12 gap-3 md:!gap-0 place-items-end">
              {formInputsBaseOnDevice.map(({ type, label, name, id }, x) => (
                <Grid.GridCol className={className(id, type)} key={id}>
                  {hasLocationSwitch && id === 0 && (
                    <LocationSwitch
                      isDisable={
                        !watch("origin" as TFormField) &&
                        !watch("destination" as TFormField)
                      }
                      handleSwtich={() => {
                        const originCopy = {
                          ...getValues("origin" as TFormField),
                        };
                        setValue(
                          "origin" as TFormField,
                          getValues("destination" as TFormField)
                        );
                        setValue("destination" as TFormField, originCopy);
                      }}
                    />
                  )}
                  <FormField
                    // @ts-ignore
                    control={control}
                    name={name as TFormField}
                    render={({ field }) => (
                      <FormItem className="relative">
                        <FormLabel value={!!field.value} variant="dynamic">
                          {services === "flight"
                            ? name === "date"
                              ? headerButton?.selected === "one-way"
                                ? (
                                    label as {
                                      "one-way": string;
                                      "round-trip": string;
                                    }
                                  )["one-way"]
                                : (
                                    label as {
                                      "one-way": string;
                                      "round-trip": string;
                                    }
                                  )["round-trip"]
                              : (label as string)
                            : (label as string)}
                        </FormLabel>
                        <FormControl>
                          <CustomInput
                            onClick={() => scrollToElement(tabRef, 500, 0)}
                            openStatus={
                              openStatus.find((item) => item.id === id)?.open ??
                              false
                            }
                            handleOpen={() => handleProgress(id)}
                            variant={type as any}
                            icon={icons[type]}
                            format={formatInputs(type, getValues)}
                            {...field}
                            readOnly={true}
                          >
                            {inputs(
                              type,
                              name as TFormField,
                              id,
                              name === "checkin"
                                ? formatIntoJalaliFunc
                                : undefined
                            )}
                          </CustomInput>
                        </FormControl>
                        <FormMessage className="absolute -bottom-[15%] right-[5%] bg-ghost" />
                      </FormItem>
                    )}
                  />
                </Grid.GridCol>
              ))}

              <Grid.GridCol className="col-span-12 lg:col-span-2 pt-6 lg:pt-0 px-1">
                <Button
                  // onClick={() => setIsDrawerOpen(false)}
                  type="submit"
                  variant="default"
                  disabled={isLoading}
                  className="!w-full h-[50px] sm:w-9/12 rounded-xs hover:!bg-background !border-[2px] !border-primary hover:!text-primary"
                >
                  {isLoading ? (
                    <Pulse />
                  ) : (
                    <Flex className="items-center gap-x-4">
                      <Typography className="!text-md">
                        {searchButton}
                      </Typography>
                      <MagnifyingGlass className="w-5 h-5" />
                    </Flex>
                  )}
                </Button>
              </Grid.GridCol>
            </Grid>
          </Flex>
        </form>
      </Form>
    </Flex>
  );
}
